/**
 * Fetch stored files through the signed-in /api/files route (story 25240).
 *
 * The transport is host-specific and installed once at boot:
 *   ora (default) — same-origin Remix route, session cookie, browser HTTP cache
 *                   (artifacts revalidate by ETag → 304s cost no bytes).
 *   add-in        — main.tsx installs a bearer fetcher (no cookie session).
 * Within a page, images are kept per path; downloads skip that cache so trace
 * zips aren't held in memory.
 */
import { storedFileName, toFileRoutePath } from "./fileRoute";

export type StoredFileFetcher = (path: string) => Promise<Response>;

// redirect: "error" — signed out, the route answers 302 to sign-in; following it
// would run the OAuth start for every image. A refused redirect is a failure.
const cookieFetcher: StoredFileFetcher = (path) => fetch(path, { credentials: "include", redirect: "error" });

let fetcher: StoredFileFetcher = cookieFetcher;

const MAX_CACHED = 40;
const blobCache = new Map<string, Promise<Blob>>();

export function setStoredFileFetcher(next: StoredFileFetcher): void {
  fetcher = next;
  blobCache.clear();
}

/** Tests only. */
export function resetStoredFileFetcher(): void {
  fetcher = cookieFetcher;
  blobCache.clear();
}

export class StoredFileError extends Error {
  constructor(
    public readonly status: number | null,
    message: string,
  ) {
    super(message);
    this.name = "StoredFileError";
  }
}

/** The OK response for `path`. A fetcher that throws synchronously still rejects (never throws). */
function request(path: string): Promise<Response> {
  let response: Promise<Response>;
  try {
    response = Promise.resolve(fetcher(path)); // called now, so the request starts in this tick
  } catch (err) {
    return Promise.reject(err);
  }
  return response.then((res) => {
    if (!res.ok) throw new StoredFileError(res.status, `HTTP ${res.status}`);
    return res;
  });
}

function load(path: string): Promise<Blob> {
  return request(path).then((res) => res.blob());
}

export function fetchStoredFile(url: string, opts: { cache?: boolean } = {}): Promise<Blob> {
  const path = toFileRoutePath(url);
  if (!path) return Promise.reject(new StoredFileError(null, "Not a stored file"));
  if (opts.cache === false) return load(path);

  const cached = blobCache.get(path);
  if (cached) {
    blobCache.delete(path); // refresh LRU position
    blobCache.set(path, cached);
    return cached;
  }
  const promise = load(path);
  blobCache.set(path, promise);
  if (blobCache.size > MAX_CACHED) {
    const oldest = blobCache.keys().next().value;
    if (oldest !== undefined) blobCache.delete(oldest);
  }
  promise.catch(() => {
    if (blobCache.get(path) === promise) blobCache.delete(path);
  });
  return promise;
}

export async function fetchStoredFileText(url: string): Promise<string> {
  return (await fetchStoredFile(url)).text();
}

export async function fetchStoredFileJson(url: string): Promise<unknown> {
  return JSON.parse(await fetchStoredFileText(url));
}

function unquote(value: string): string {
  const v = value.trim();
  return v.startsWith('"') && v.endsWith('"') && v.length >= 2 ? v.slice(1, -1).replace(/\\(.)/g, "$1") : v;
}

/**
 * The file name in a Content-Disposition header: RFC 5987 `filename*` (UTF-8,
 * percent-decoded) first, then `filename`. Only the base name is kept.
 */
export function contentDispositionFileName(header: string | null | undefined): string | null {
  if (!header) return null;
  let name: string | null = null;
  const extended = /(?:^|;)\s*filename\*\s*=\s*([^;]*)/i.exec(header);
  const encoded = extended && /^utf-8'[^']*'(.*)$/i.exec(unquote(extended[1]));
  if (encoded) {
    try {
      name = decodeURIComponent(encoded[1]);
    } catch {
      name = null;
    }
  }
  if (!name) {
    const plain = /(?:^|;)\s*filename\s*=\s*("(?:[^"\\]|\\.)*"|[^;]*)/i.exec(header);
    name = plain ? unquote(plain[1]) : null;
  }
  const base = name?.split(/[\\/]/).pop()?.trim();
  return base || null;
}

export async function downloadStoredFile(url: string, filename?: string | null): Promise<void> {
  const path = toFileRoutePath(url);
  if (!path) throw new StoredFileError(null, "Not a stored file");
  const res = await request(path); // never cached: trace zips are not kept in memory
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download =
    filename?.trim() || contentDispositionFileName(res.headers?.get("Content-Disposition")) || storedFileName(url);
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000); // Firefox/Safari can cancel the download if revoked sooner
}
